<template>
  <div v-if="props.qrEnabled && props.tableName">
    <!-- At a table. Table and people sit on one row because they are one fact
         — which table, and who is ordering onto it. -->
    <div class="flex flex-col gap-1 text-caption text-muted">
      <div class="flex flex-wrap items-center gap-2">
        <span class="font-medium text-ink">At {{ props.tableName }}</span>

        <span
          v-if="visiblePeople.length"
          class="flex items-center gap-1"
        >
          <template
            v-for="(person, index) in visiblePeople"
            :key="`${person.initials}-${index}`"
          >
            <img
              v-if="person.avatarUrl"
              alt=""
              class="h-6 w-6 rounded-full object-cover"
              :src="person.avatarUrl"
              :title="person.name"
            />
            <span
              v-else
              class="flex h-6 w-6 items-center justify-center rounded-full text-[11px]"
              :style="chipStyle(person.initials, index)"
              :title="person.name"
            >
              {{ person.initials }}
            </span>
          </template>

          <span
            v-if="overflow > 0"
            class="flex h-6 w-6 items-center justify-center rounded-full border bg-card text-[11px] text-muted"
          >
            +{{ overflow }}
          </span>
        </span>

        <template v-if="props.hasOpenTab">
          <span class="text-muted/60">·</span>
          <Link
            class="pj-tap font-semibold text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
            :href="props.tabHref"
          >
            See the bill
          </Link>
        </template>
      </div>

      <p
        v-if="!props.hasOpenTab"
        class="text-caption text-muted"
      >
        Your order goes to this table.
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'

import type { PjazzaTabParticipant } from '@/domains/pjazza/types/models/PjazzaTab.types.ts'

import { PJ_TILE_TINT, tintFor } from '@/domains/pjazza/utils/tileTint.ts'

const props = defineProps<{
  qrEnabled?: boolean
  tableName?: string | null
  /** False while the table is only this device's choice, with no rounds yet. */
  hasOpenTab?: boolean
  participants?: PjazzaTabParticipant[]
  tabHref: string
}>()

/**
 * Who else is ordering onto your table.
 *
 * Lives in the venue hero rather than on the tab page. The tab page is a screen
 * you visit deliberately, and nobody visits it mid-meal — they sit on the menu.
 * Putting this in the hero means "somebody else just ordered" shows up where
 * diners already are, and the tab page keeps the detail.
 *
 * Renders nothing until there is a table. Getting to one is the nav's job.
 */
const AVATAR_CAP = 4

const people = computed(() => props.participants?.length ?? 0)

const visiblePeople = computed(() => (props.participants ?? []).slice(0, AVATAR_CAP))

const overflow = computed(() => Math.max(0, people.value - AVATAR_CAP))

// Colour distinguishes, initials hint: two people at one table can share
// initials, so position is mixed into the hash to break a tint collision.
const chipStyle = (initials: string, index: number): Record<string, string> => {
  const tint = tintFor(`${initials}${index % PJ_TILE_TINT.length}`)

  return {
    backgroundColor: `var(--pj-tint-${tint})`,
    color: `var(--pj-tint-${tint}-ink)`
  }
}
</script>
